// @ts-strict-ignore
import {Component, effect, inject, input, OnInit} from "@angular/core";
import {ClientType, Device, WebAudioClientMapping} from "@models";
import {EventEmitterService} from "@event-emitter";
import {NotificationService, SimConfigurationService, SimMaterialDialogService, WebsocketAudioClient} from "@services";
import {
	DialogData_DeleteDialog,
	DialogData_DeleteDialogOutput,
} from "../../../modules/dialog/material-dialogs/general-dialogs/delete-dialog-mat/delete-dialog-mat.component";
import {explicitEffect} from "ngxtension/explicit-effect";
import {Translations} from "@translations";

@Component({
	selector: "audio-player",
	templateUrl: "./audio-player.component.html",
	providers: [EventEmitterService, WebsocketAudioClient],
	standalone: false,
})
export class AudioPlayerComponent implements OnInit {
	//  I/O
	public webClient = input.required<WebAudioClientMapping | Device>();
	public trainingRoomId = input<string>();
	public webClientType = input<ClientType>();
	public webClientMasterId = input<string>();
	public preferredFrameSize = input<number>();
	public ringBufferLength = input<number>();
	public muteWebsocketAudio = input<boolean>();
	public requestVolumeData = input<boolean>();
	public requestLatencyData = input<boolean>();
	public announcementSessionId = input<string>();
	/**
	 * controls the final output gain node. Can have values between 0 and 100
	 */
	public audioOutputVolume = input<number>();
	public audioOutputMuted = input<boolean>();

	/**
	 * multiplies the volume data used for displaying the peakmeter.
	 */
	public audioVolumeMultiplier = input<number>(SimConfigurationService.settings.audioVolumeMultiplier);
	public audioVolumeMax = input<number>(SimConfigurationService.settings.audioVolumeMax);

	// Constants
	protected readonly T = Translations;

	// Injections
	readonly #websocketAudioClient = inject(WebsocketAudioClient);
	readonly #notificationService = inject(NotificationService);
	readonly #dialogService = inject(SimMaterialDialogService);
	readonly #emitterService = inject(EventEmitterService);

	// Effects
	readonly #startLiveAudio = explicitEffect(
		[this.trainingRoomId, this.webClientMasterId, this.announcementSessionId],
		async () => await this.startLiveAudio(),
	);
	readonly #modulateVolume = effect(() => {
		if (0 <= this.audioOutputVolume() && this.audioOutputVolume() <= 100) {
			if (this.#websocketAudioClient) {
				this.#websocketAudioClient.setAudioOutputVolume(this.audioOutputVolume());
			}
		}
	});
	readonly #muteAudio = effect(() => {
		if (this.audioOutputMuted() !== null) {
			if (this.#websocketAudioClient) {
				this.#websocketAudioClient.setAudioOutputMuted(this.audioOutputMuted());
			}
		}
	});

	public ngOnInit() {
		this.#emitterService.onAudioPlaybackNeedsSecureContext.subscribe(() =>
			this.openAudioModal(
				"Audio for this application needs a secure context. Please enable https and secure websockets for the webserver.",
			),
		);

		this.#emitterService.onAudioNeedsUserConfirmation.subscribe(() =>
			this.openAudioModal(
				"Audio for this application is not enabled on this device. See: https://developers.google.com/web/updates/2017/09/autoplay-policy-changes#webaudio for more informations.",
			),
		);
	}

	private openAudioModal(description: string) {
		const data: DialogData_DeleteDialog = {
			width: 500,
			deleteTitle: "Audio Problem",
			deleteText: description,
			deleteButtonText: "OK",
		};
		const dialogRef = this.#dialogService.openDeleteDialog(data);
		dialogRef.then(async (result: DialogData_DeleteDialogOutput) => {
			if (result?.action === "ok") {
				await this.#websocketAudioClient.resumeAudio();
			}
		});
	}

	private async startLiveAudio() {
		if (this.trainingRoomId() || this.announcementSessionId()) {
			const {success, reason} = await this.#websocketAudioClient.initializeAudio(
				SimConfigurationService.settings.websocketAudioEndpoint,
				SimConfigurationService.settings.audioBufferSize,
				this.webClientType(),
				this.webClientMasterId(),
				this.webClient(),
				this.preferredFrameSize() || (this.webClient() as WebAudioClientMapping)?.preferedFrameSize || 1024,
				this.ringBufferLength() || (this.webClient() as WebAudioClientMapping)?.ringBufferLength || 20480,
				this.muteWebsocketAudio() ?? (this.webClient() as WebAudioClientMapping)?.muteWebsocketAudio ?? false,
				this.audioVolumeMultiplier(),
				this.audioVolumeMax(),
				this.audioOutputVolume(),
				this.audioOutputMuted(),
				this.requestVolumeData(),
				this.requestLatencyData(),
				this.trainingRoomId(),
				this.announcementSessionId(),
			);
			if (!success) {
				this.#notificationService.notifyError(`Audio client could not be initialized. Reason: ${reason}`);
			}
		}
	}
}
